import { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { LOCALES, DEFAULT_LOCALE, localeNames, localePath, type Lang } from '../lib/i18n';
import { postAltHref } from '../lib/blog';

function basePath(pathname: string): { lang: Lang; path: string } {
  const seg = pathname.split('/')[1];
  if ((LOCALES as readonly string[]).includes(seg) && seg !== DEFAULT_LOCALE) {
    return { lang: seg as Lang, path: pathname.slice(seg.length + 1) || '/' };
  }
  return { lang: DEFAULT_LOCALE, path: pathname || '/' };
}

export function LangSwitcher({ lang, className = '' }: { lang: Lang; className?: string }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const { path } = basePath(pathname);
  const postMatch = path.match(/^\/blog\/(.+)$/);
  const hrefFor = (target: Lang) =>
    postMatch ? postAltHref(lang, postMatch[1], target) : localePath(target, path);

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Change language"
        className="btn-3d btn-3d--ghost btn-3d--sm gap-1.5 px-3.5 py-2"
      >
        {localeNames[lang]}
        <span className="text-faint text-xs" aria-hidden="true">▾</span>
      </button>
      {open && (
        <ul
          role="listbox"
          className="pop-in absolute right-0 bottom-auto mt-3 min-w-[10rem] card-float border-2 border-line p-1.5 z-50"
        >
          {LOCALES.map((l) => (
            <li key={l}>
              <a
                href={hrefFor(l)}
                className={`block px-3 py-2 rounded-xl text-sm font-semibold transition-colors ${
                  l === lang
                    ? 'text-[var(--brand-ink)] bg-[var(--brand-soft)]'
                    : 'text-muted hover:text-ink hover:bg-ink/5'
                }`}
                aria-current={l === lang ? 'true' : undefined}
              >
                {localeNames[l]}
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
